import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type { ResponseBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";

type SpendingAccountBalancesEndpoint =
  paths["/{tenantId}/patients/{patientId}/spending-account-balances"];

export type ApiSpendingAccountBalance = NonNullable<
  ResponseBody<SpendingAccountBalancesEndpoint["get"]>["data"]
>[number];

export type SpendingAccountType = ApiSpendingAccountBalance["accountType"];

export function spendingAccountBalancesQueryKey(patientId: string | null) {
  return ["spending-account-balances", patientId] as const;
}

function createIdempotencyKey() {
  const runtimeCrypto = globalThis.crypto;
  if (runtimeCrypto?.randomUUID) return runtimeCrypto.randomUUID();

  const hex = () =>
    Math.floor(Math.random() * 0x10000)
      .toString(16)
      .padStart(4, "0");
  return `${hex()}${hex()}${hex()}${hex()}-${hex()}-4${hex().slice(1)}-8${hex().slice(1)}-${hex()}${hex()}${hex()}`;
}

export function useSpendingAccountBalances() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const queryKey = spendingAccountBalancesQueryKey(patientId);

  const query = useQuery<ApiSpendingAccountBalance[]>({
    queryKey,
    queryFn: async () => {
      if (!tenantId || !patientId) return [];
      const { data, error, response } = await client.GET(
        "/{tenantId}/patients/{patientId}/spending-account-balances",
        { params: { path: { tenantId, patientId } } },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to load spending account balances",
          "useSpendingAccountBalances",
          response?.status,
        );
      }
      return data?.data ?? [];
    },
    enabled: !!tenantId && !!patientId,
    placeholderData: [],
  });

  const saveMutation = useMutation({
    mutationFn: async ({
      accountType,
      balanceCents,
    }: {
      accountType: SpendingAccountType;
      balanceCents: number;
    }) => {
      if (!tenantId || !patientId) throw new Error("No patient context");
      const { data, error, response } = await client.PUT(
        "/{tenantId}/patients/{patientId}/spending-account-balances/{accountType}",
        {
          params: {
            path: { tenantId, patientId, accountType },
            header: { "Idempotency-Key": createIdempotencyKey() },
          },
          body: { balanceCents },
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to save spending account balance",
          "useSpendingAccountBalances",
          response?.status,
        );
      }
      if (!data?.data) throw new Error("Spending account response was empty");
      return data.data;
    },
    onSuccess: (balance) => {
      queryClient.setQueryData<ApiSpendingAccountBalance[]>(
        queryKey,
        (current = []) => [
          ...current.filter((item) => item.accountType !== balance.accountType),
          balance,
        ],
      );
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const clearMutation = useMutation({
    mutationFn: async (accountType: SpendingAccountType) => {
      if (!tenantId || !patientId) throw new Error("No patient context");
      const { error, response } = await client.DELETE(
        "/{tenantId}/patients/{patientId}/spending-account-balances/{accountType}",
        { params: { path: { tenantId, patientId, accountType } } },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to clear spending account balance",
          "useSpendingAccountBalances",
          response?.status,
        );
      }
    },
    onSuccess: (_result, accountType) => {
      queryClient.setQueryData<ApiSpendingAccountBalance[]>(
        queryKey,
        (current = []) =>
          current.filter((item) => item.accountType !== accountType),
      );
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    ...query,
    saveBalance: saveMutation.mutateAsync,
    clearBalance: clearMutation.mutateAsync,
    isSaving: saveMutation.isPending,
    saveError: saveMutation.error,
  };
}
